import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useToast } from "@chakra-ui/react";
import { googleCreateAsync, loginUserAsync } from "../authSlices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import "./Login.css";

// Assuming these images are in the correct path
import bg from "../image/logologin.png";
import googlelogo from "../image/googlelogo.png";
import { signInWithGoogle } from "../auth";

// ScreenLoader component with blue animation
const ScreenLoader = () => {
  return (
    <div className="loader-container">
      <div className="loader"></div>
    </div>
  );
};

export default function Login() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    // Preload the background image
    const img = new Image();
    img.src = bg;
    img.onload = () => {
      setIsMounted(true);
    };
  }, []);

  const handleLoginSuccess = (user, loginType = "form") => {
    // Common success handler for both form and Google login
    const successMessage = loginType === "google"
      ? "You have successfully logged in with Google."
      : "You have successfully logged in.";

    toast({
      title: "Login successful.",
      description: successMessage,
      status: "success",
      duration: 5000,
      isClosable: true,
      position: "top-right",
    });

    // Check user type and handle accordingly
    if (user.user.user_type === "candidate") {
      // Only check profile completion for candidates
      if (user.is_profile_completed === false) {
        navigate("/phone-verification");
        return;
      }

      // Check if there's a stored redirect path for candidates
      const redirectPath = localStorage.getItem("redirectAfterLogin");
      if (redirectPath) {
        localStorage.removeItem("redirectAfterLogin"); // Clean up
        navigate(redirectPath); // Redirect to the original page
      } else {
        navigate("/"); // Default redirect if no stored path
      }
    } else if (user.user.user_type === "admin") {
      // Admin users go directly to admin dashboard
      navigate("/admin-dashboard");
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const user = await dispatch(loginUserAsync({
        email: data.email,
        password: data.password
      })).unwrap();

      if (user.user.user_type === "employer") {
        toast({
          title: "Login Unsuccessful",
          description: "This is candidate login only.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }

      handleLoginSuccess(user);
    } catch (e) {
      // Display error toast
      toast({
        title: "Login failed.",
        description: "Invalid email or password.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      console.log("Error in Login", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      // Sign in with Google
      const { accessToken } = await signInWithGoogle();
      const data = {
        accessToken,
        user_type: "candidate", // Force user_type to candidate for Google login
      };

      // Send accessToken to API
      const response = await dispatch(googleCreateAsync(data)).unwrap();

      console.log("Google Create Response:", response);

      if (response?.login_type === "login") {
        if (response?.user?.user_type === "candidate") {
          handleLoginSuccess(response, "google");
        } else {
          toast({
            title: "Login Unsuccessful",
            description: "This is candidate login only.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
        }
      } else if (response?.login_type === "register") {
        // New Google users always go to phone verification
        navigate("/phone-verification");
      }
    } catch (error) {
      toast({
        title: "Google login failed.",
        description: "There was an error logging in with Google.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      console.error("Google login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  if (!isMounted) {
    return <ScreenLoader />;
  }

  return (
    <div className="min-h-screen maindiv flex items-center justify-center px-3 py-6 font-p">
      {isLoading && <ScreenLoader />}
      <div className="flex flex-col md:flex-row bg-white shadow-lg rounded-lg overflow-hidden max-w-6xl w-full">
        {/* Left Panel - Login Form */}
        <div className="w-full md:w-1/2 p-4 sm:p-5">
          <h1 className="text-left font-semibold text-2xl text-textBlue mb-1">IndiaHirings.com</h1>
          <p className="mt-2 text-left text-gray-600">Welcome back! Please log in to your Candidate account.</p>

          <form className="mt-4" noValidate onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-mail <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                className={`w-full mt-2 px-3 py-2 text-sm border ${errors.email ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all`}
                placeholder="Enter your email"
                {...register("email", { required: "Email is required" })}
              />
              <div className="h-5">
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className={`w-full mt-2 px-3 py-2 text-sm border ${errors.password ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all`}
                  placeholder="Enter your password"
                  {...register("password", { required: "Password is required" })}
                />
                <button
                  type="button"
                  className="absolute right-3 top-4 text-gray-500 hover:text-gray-700 text-sm"
                  onClick={togglePasswordVisibility}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              <div className="h-5">
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
              </div>
            </div>

            <div className="flex items-center justify-between mb-3">
              <Link
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                to="/reset-code-request"
              >
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              className="w-full mt-6 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md transition duration-300 shadow-sm hover:shadow-md"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Login"}
            </button>

            <div className="relative my-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white text-gray-500">OR</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              className="flex items-center justify-center py-3 px-6 bg-white hover:bg-gray-50 border border-gray-300 rounded-md w-full transition duration-300 shadow-sm hover:shadow-md"
              disabled={isLoading}
            >
              <img src={googlelogo || "/placeholder.svg"} alt="Google Logo" className="h-7 w-7 object-cover" />
              <span className="ml-2">Sign In with Google</span>
            </button>

            <div className="text-center mt-4">
              <p className="text-sm text-gray-600">
                Don't have an account?{" "}
                <Link
                  to="/signup"
                  className="text-blue-600 font-medium hover:text-blue-800 hover:underline transition-colors text-sm"
                >
                  Sign up here
                </Link>
              </p>
            </div>
          </form>
        </div>

        <div className="hidden md:block md:w-1/2 relative">
          <div className="absolute inset-0 bg-black bg-opacity-50 z-10"></div>
          <img src={bg || "/placeholder.svg"} className="object-cover h-full w-full" alt="Background" />
          <div className="absolute inset-0 z-20 flex items-center justify-center p-6">
            <div className="text-white max-w-xs">
              <h2 className="text-2xl font-bold mb-3">Find Your Dream Job</h2>
              <p className="text-lg leading-relaxed">
                Connect with top employers and discover opportunities that match your skills and aspirations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}